import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { readMfaState, type MfaState } from '../lib/mfa';
import { useNavigate } from 'react-router-dom';

const normalizeRole = (role?: string | null) => role?.trim?.().toLowerCase?.();

interface User {
  id: string;
  email: string;
  role: string;
  name: string;
  avatar?: string | null;
  agency_id?: string;
}

interface UserContextType {
  user: User | null;
  isAuthenticated: boolean;
  isUserLoaded: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  /** Where this session stands on the code step. The app stays behind TwoFactorGate until it is 'ok'. */
  mfaState: MfaState;
  /** Re-reads mfaState (after enrolling or entering a code) and reloads the profile once it is 'ok'. */
  refreshMfa: () => Promise<MfaState>;
  getDefaultRoute: () => string;
  logout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem('user');
    if (!savedUser) return null;

    const parsedUser = JSON.parse(savedUser);
    return {
      ...parsedUser,
      role: normalizeRole(parsedUser.role) ?? parsedUser.role
    };
  });
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const [mfaState, setMfaState] = useState<MfaState>('checking');
  const navigate = useNavigate();

  /** The profile row, readable only once the session is aal2 (the database refuses aal1). */
  const loadProfile = useCallback(async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    const authUser = sessionData.session?.user;
    if (!authUser) return;
    const { id, email } = authUser;
    const { data: userData, error } = await supabase.from('users').select('role, name, avatar, agency_id').eq('id', id).single();
    if (error || !userData) return;
    const fullUser = {
      id,
      email: email || '',
      role: normalizeRole(userData.role) || 'authenticated',
      name: userData.name || email?.split('@')[0] || 'User',
      avatar: userData.avatar || null,
      agency_id: userData.agency_id,
    };
    setUser(fullUser);
    localStorage.setItem('user', JSON.stringify(fullUser));
  }, []);

  const refreshMfa = useCallback(async () => {
    const next = await readMfaState();
    setMfaState(next);
    if (next === 'signed-out') {
      // A user remembered in localStorage with no live session (sessions ended on the server): back to login.
      setUser(null);
      localStorage.removeItem('user');
    }
    if (next === 'ok') await loadProfile();
    return next;
  }, [loadProfile]);

  // Listen for auth state changes
  useEffect(() => {
    // Get initial session
    refreshMfa();
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        const { id, email } = session.user;

        // Fetch the actual role from the public users table FIRST
        // Promise.resolve: the query builder is thenable, not a Promise, so .finally/.catch need a real one.
        Promise.resolve(supabase.from('users').select('role, name, avatar, agency_id').eq('id', id).single())
          .then(({ data: userData, error }) => {
            if (!error && userData) {
              const normalizedRole = normalizeRole(userData.role);

              const fullUser = {
                id,
                email: email || '',
                role: normalizedRole || 'authenticated',
                name: userData.name || email?.split('@')[0] || 'User',
                avatar: userData.avatar,
                agency_id: userData.agency_id
              };
              setUser(fullUser);
              localStorage.setItem('user', JSON.stringify(fullUser));
            } else {
              // If we can't fetch user data, use basic auth info
              const basicUser = {
                id,
                email: email || '',
                name: email?.split('@')[0] || 'User',
                role: 'authenticated',
                avatar: null,
                agency_id: undefined
              };
              setUser(basicUser);
              localStorage.setItem('user', JSON.stringify(basicUser));
            }
          })
          .finally(() => {
            setIsUserLoaded(true);
          });
      } else {
        setIsUserLoaded(true);
      }
    });

    // Set up auth state listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'MFA_CHALLENGE_VERIFIED') {
        refreshMfa();
        return;
      }
      if (event === 'SIGNED_IN' && session?.user) {
        refreshMfa();
        const { id, email } = session.user;

        // Fetch the actual role from the public users table FIRST
        // Promise.resolve: the query builder is thenable, not a Promise, so .finally/.catch need a real one.
        Promise.resolve(supabase.from('users').select('role, name, avatar, agency_id').eq('id', id).single())
          .then(({ data: userData, error }) => {
            if (!error && userData) {
              const normalizedRole = normalizeRole(userData.role);

              const fullUser = {
                id,
                email: email || '',
                role: normalizedRole || 'authenticated',
                name: userData.name || email?.split('@')[0] || 'User',
                avatar: userData.avatar || null,
                agency_id: userData.agency_id
              };
              setUser(fullUser);
              localStorage.setItem('user', JSON.stringify(fullUser));
            } else {
              // If we can't fetch user data, use basic auth info
              const basicUser = {
                id,
                email: email || '',
                name: email?.split('@')[0] || 'User',
                role: 'authenticated',
                avatar: null,
                agency_id: undefined
              };
              setUser(basicUser);
              localStorage.setItem('user', JSON.stringify(basicUser));
            }
            setIsUserLoaded(true);
          })
          .catch(() => {
            setIsUserLoaded(true);
          });
      } else if (event === 'SIGNED_OUT') {
        setMfaState('signed-out');
        setUser(null);
        localStorage.removeItem('user');
        navigate('/login');
        setIsUserLoaded(true);
      }
    });

    // Cleanup subscription
    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, refreshMfa]);

  const isAuthenticated = user !== null;

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      
      // The auth state change handler will update the user state
      // with the role from the public users table
      return true;
    } catch (error) {
      console.error('Error logging in:', error);
      return false;
    }
  };

  // Function to determine the default route based on user role
  const getDefaultRoute = () => {
    if (!user) return '/login';

    // Explicitly check for client role first
    if (normalizeRole(user.role) === 'client') {
      return '/private-network';
    }

    // Handle other roles
    switch (normalizeRole(user.role)) {
      case 'client':
        return '/private-network';
      case 'admin':
      case 'developer':
      case 'authenticated':
      case 'agency_owner':
      default:
        return '/clients';
    }
  };

  // Function to delete all cookies
  const deleteAllCookies = () => {
    const cookies = document.cookie.split(";");
    
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      
      // Delete cookies on all potential paths and domains
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname;
      
      // Also try with the domain prefixed with a dot (for cross-subdomain cookies)
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=." + window.location.hostname;
    }
    
    // Specifically target Supabase cookies if any
    const supabaseCookieNames = ['sb-refresh-token', 'sb-access-token', 'supabase-auth-token'];
    supabaseCookieNames.forEach(name => {
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname;
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=." + window.location.hostname;
    });
  };

  const logout = async () => {
    try {
      // First sign out from Supabase
      await supabase.auth.signOut();
      
      // Clear user data from state and localStorage
      setUser(null);
      localStorage.removeItem('user');
      
      // Clear all browser cookies
      deleteAllCookies();
      
      // Also clear any localStorage items related to Supabase
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
          localStorage.removeItem(key);
        }
      }
      
      // Small delay to ensure everything is cleared
      setTimeout(() => {
        // Force a full page reload to /login
        window.location.href = '/login';
      }, 100);
    } catch (error) {
      console.error('Error during logout:', error);
      
      // Fallback: clear everything and force redirect
      setUser(null);
      localStorage.removeItem('user');
      deleteAllCookies();
      window.location.href = '/login';
    }
  };

  return (
    <UserContext.Provider value={{ user, isAuthenticated, isUserLoaded, login, logout, getDefaultRoute, mfaState, refreshMfa }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  
  return context;
}